/**
 * useEntityMediaLimit – unified hook for media limits across all entity types.
 * Replaces useProducerMediaLimit and useActivityMediaLimit.
 *
 * For producer: uses 'media_images_max' feature key (default 4)
 * For event/activity: uses 'media_activity_images_max' feature key (default 3)
 * For other types: returns unlimited (999)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OwnerType } from '@/lib/media/mediaTypes';

export interface EntityMediaLimit {
  currentCount: number;
  maxImages: number;
  remaining: number;
  isAtLimit: boolean;
}

const FEATURE_MAP: Partial<Record<OwnerType, { key: string; fallback: number }>> = {
  producer: { key: 'media_images_max', fallback: 4 },
  producer_shop: { key: 'media_images_max', fallback: 8 },
  event: { key: 'media_activity_images_max', fallback: 3 },
  product: { key: 'media_product_images_max', fallback: 4 },
};

export function useEntityMediaLimit(
  ownerType: OwnerType,
  ownerId: string | null | undefined,
  producerId: string | undefined,
): {
  data: EntityMediaLimit;
  isLoading: boolean;
} {
  const config = FEATURE_MAP[ownerType];
  const fallback = config?.fallback ?? 999;
  const defaults: EntityMediaLimit = { currentCount: 0, maxImages: fallback, remaining: fallback, isAtLimit: false };

  const effectiveProducerId = (ownerType === 'producer' || ownerType === 'producer_shop') ? ownerId ?? producerId : producerId;

  const { data, isLoading } = useQuery({
    queryKey: ['entity-media-limit', ownerType, ownerId, effectiveProducerId],
    queryFn: async (): Promise<EntityMediaLimit> => {
      // No config = unlimited (place, product, ui)
      if (!config || !effectiveProducerId) return defaults;

      // Get limit from producer's effective features
      const { data: features } = await supabase
        .from('producer_effective_features_v')
        .select('limit_value')
        .eq('producer_id', effectiveProducerId)
        .eq('feature_key', config.key)
        .maybeSingle();

      const maxImages = features?.limit_value ?? fallback;

      // Count current media for this entity
      if (!ownerId) return { currentCount: 0, maxImages, remaining: maxImages, isAtLimit: false };

      const { count } = await supabase
        .from('media_assets')
        .select('*', { count: 'exact', head: true })
        .eq('owner_type', ownerType)
        .eq('owner_id', ownerId)
        .in('kind', ['cover', 'gallery']);

      const currentCount = count ?? 0;
      const remaining = Math.max(0, maxImages - currentCount);

      return {
        currentCount,
        maxImages,
        remaining,
        isAtLimit: currentCount >= maxImages,
      };
    },
    enabled: !!effectiveProducerId || !config,
    // Phase 1 stability: raised from 10 s; count only changes after an upload/delete which
    // already triggers a query invalidation in MediaImageManager. 60 s prevents continuous
    // polling of media_assets in editor sessions.
    staleTime: 60_000,
  });

  return {
    data: data ?? defaults,
    isLoading,
  };
}
